import {useEffect, useState} from "react";
import {Loader} from "@mantine/core";
import {Feature, useFeature} from "../../featureFlags/featureFlags";
import {dibAppApi} from "../../dibAppApi/dibAppApi";
import {TopBarSlot} from "../chat/TopBarContext";
import {AccessContext, type AccessEntry} from "./AccessContext";
import {useContentParams} from "./router";
import {ChecklistResource} from "./ChecklistResource";
import {FavoriteTopicButton} from "./FavoriteTopicButton";
import {DocumentGeneratorResource} from "./DocumentGeneratorResource";
import {ExternalFallback} from "./ExternalFallback";
import {HTMLResource} from "./HTMLResource";
import {HTMLResourceLEGACY} from "./legacy/HTMLResourceLEGACY";
import {NoAccessModal} from "./NoAccessModal";
import {ResourceError} from "./ResourceError";
import {StatusMessage} from "./StatusMessage";
import {ResourceType} from "./types";
import layoutClasses from "./ContentLayout.module.css";
import classes from "./ContentPage.module.css";
import {PanelProvider} from "./panel/PanelContext";
import {SliderPanel} from "./panel/SliderPanel";

export const ContentPage = () => {
    const {enabled: canViewInteractiveContent, isLoading: isUserDataLoading} =
        useFeature(Feature.ViewDibContent);
    const {enabled: newContentDesign} = useFeature(Feature.NewContentDesign);
    const {
        resourceId,
        dokumentId,
        collectionId,
        segmentId,
        isValidTopicType,
        isPreview,
    } = useContentParams();

    const isSkipped = !resourceId || (!isPreview && !isValidTopicType);

    // Topic-level access verdict, reported by whichever leaf loaded the topic
    // data (see AccessContext), tagged with the leaf's resourceId. A stale
    // entry from a previous topic simply stops matching, which both clears the
    // gate on navigation and keeps it up across intra-topic segment fetches.
    const [accessEntry, setAccessEntry] = useState<AccessEntry | null>(null);
    const noAccess =
        accessEntry !== null &&
        accessEntry.id === resourceId &&
        !accessEntry.access;
    const {
        data: resourceTypeId,
        isLoading: isPrefetchLoading,
        isError,
        error,
    } = dibAppApi.endpoints.getResourceTypeId.useQuery(
        {topicId: resourceId ?? ""},
        {skip: isSkipped}
    );

    // Title is owned by the leaf resource components (they set
    // `<topic name> - KAI` once their data loads). ContentPage covers the
    // windows where no leaf is mounted: keep "KAI" while resolving the resource
    // type / skipped / errored, and reset on leaving any content route.
    useEffect(() => {
        if (isSkipped || isPrefetchLoading || isError) {
            document.title = "KAI";
        }
    }, [isSkipped, isPrefetchLoading, isError]);

    useEffect(() => {
        return () => {
            document.title = "KAI";
        };
    }, []);

    // Render nothing — neither inline content nor ExternalFallback — until
    // user data resolves: the ViewDibContent bit is only final after that,
    // and an opted-in user must not see a fallback flash.
    if (isUserDataLoading) return null;

    const getContent = () => {
        if (isSkipped) {
            return (
                <StatusMessage
                    title="Content not found"
                    detail="The page you're looking for doesn't exist or the URL is invalid."
                />
            );
        }

        if (isPrefetchLoading) {
            return (
                <div className={layoutClasses.statusMessage}>
                    <Loader color="gneis.5" size="md" />
                </div>
            );
        }

        if (isError) {
            return <ResourceError error={error} />;
        }

        // Gatekeeper for everything this page can't (or may not) render
        // in-app: unsupported resource types, and checklist/docgen without
        // the ViewDibContent opt-in.
        const externalFallback = (
            <ExternalFallback resourceId={resourceId!} segmentId={segmentId} />
        );

        switch (resourceTypeId) {
            case ResourceType.html:
            case ResourceType.staticHtml:
            case ResourceType.skatteAbc:
            case ResourceType.dibDocumentFormat: {
                const ResourceComponent = newContentDesign
                    ? HTMLResource
                    : HTMLResourceLEGACY;
                return (
                    <ResourceComponent
                        resourceId={resourceId!}
                        segmentId={segmentId}
                    />
                );
            }
            case ResourceType.checklist:
                if (!canViewInteractiveContent) return externalFallback;
                return (
                    <ChecklistResource
                        resourceId={resourceId!}
                        collectionId={collectionId}
                        dokumentId={dokumentId}
                        isPreview={isPreview}
                    />
                );
            case ResourceType.documentGenerator:
                if (!canViewInteractiveContent) return externalFallback;
                return (
                    <DocumentGeneratorResource
                        resourceId={resourceId!}
                        isPreview={isPreview}
                    />
                );
            default:
                return externalFallback;
        }
    };

    const showFavorite =
        !isSkipped && !isPreview && !isError && resourceTypeId !== undefined;

    return (
        <PanelProvider>
            <AccessContext.Provider value={setAccessEntry}>
                {showFavorite && (
                    <TopBarSlot slot="right">
                        <FavoriteTopicButton topicId={resourceId!} />
                    </TopBarSlot>
                )}
                <div className={classes.contentPage}>{getContent()}</div>
                <NoAccessModal opened={noAccess} />
                <SliderPanel />
            </AccessContext.Provider>
        </PanelProvider>
    );
};
